<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDebounce } from '@vueuse/core';
import { vMaska } from 'maska/vue';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';
import { isMaskedPhoneCountry } from '@Constants';

const FREE_TEXT_MAX_LENGTH = 20;
const MASKED_PLACEHOLDER = '(999) 999-9999';

const emits = defineEmits<{
  blur: [FocusEvent];
  focus: [FocusEvent];
  'update:modelValue': [string | null | undefined];
}>();

const props = withDefaults(
  defineProps<{
    debounceTime?: number;
    disabled?: boolean;
    error?: string | boolean;
    field?: FieldSchema;
    inProgress?: boolean;
    inputClass?: string;
    modelValue: string | undefined | null;
    placeholder?: string;
    preventTab?: boolean;
    required?: boolean;
  }>(),
  {
    debounceTime: 300,
    placeholder: MASKED_PLACEHOLDER,
    preventTab: false,
  }
);

const { t } = useI18n();

const _model = ref(props.modelValue || '');

const input = useDebounce(_model, props.debounceTime);

const activeModelProp = computed(() => props?.field?.model || props.modelValue);
const settings = computed(() => props?.field?.settings);
// NOTE :: `settings.country` is for a form about ANOTHER shelter (admin create shelter); otherwise the active shelter's country.
const isMasked = computed(() => isMaskedPhoneCountry(settings.value?.country));

const disabledClass = computed(() => ({
  'pointer-events-none cursor-default bg-[#E2E8F0] text-[#7A889D]': props.disabled,
}));
// NOTE :: Only the US-mask default is swapped for the app's generic field hint — a caller's own
// placeholder (`settings.placeholder` or the prop) still wins, since it is already country-neutral.
const freeTextPlaceholder = computed(() => {
  const callerPlaceholder = settings.value?.placeholder || props.placeholder;

  return callerPlaceholder === MASKED_PLACEHOLDER ? t('components.forms.placeholder.common') : callerPlaceholder;
});

const inputClassList = computed(() => [
  'h-9 w-full !border-gray-300 text-sm text-gray-700 focus:!border-green-500 focus:!ring-0',
  props.inputClass ?? 'rounded-md',
  { 'af-field-error !border-red-600': !!props.error },
  disabledClass.value,
]);

watch(activeModelProp, (newValue) => (_model.value = newValue));
watch(input, () => emits('update:modelValue', _model.value));

// HELPERS
// NOTE :: Letters are dropped, so `phone` stays bare digits — table search, exports and the import
// cleaners all rely on that. An extension typed as "x77" is therefore not kept; `phone_extension`
// exists for data that already carries one, and typing one is a later task.
function getFreeTextPhone(value: string) {
  return value.replace(/[^\d\s()+.-]/g, '').slice(0, FREE_TEXT_MAX_LENGTH);
}

// EVENTS
function handleKeyDown(event: KeyboardEvent) {
  if (props.preventTab && event.key === 'Tab') return event.preventDefault();
}

function onFreeTextInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const phone = getFreeTextPhone(target.value);

  target.value = phone;
  _model.value = phone;
}
</script>

<template>
  <div class="w-full">
    <input
      v-if="isMasked"
      v-model="_model"
      v-maska
      data-maska="['(###) ###-####', '#(###) ###-####', '##(###) ###-####', '###(###) ###-####']"
      type="tel"
      :class="inputClassList"
      :placeholder="settings?.placeholder || placeholder"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @keydown="handleKeyDown"
    />

    <input
      v-else
      inputmode="tel"
      type="tel"
      :class="inputClassList"
      :maxlength="FREE_TEXT_MAX_LENGTH"
      :placeholder="freeTextPlaceholder"
      :value="_model"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @input="onFreeTextInput"
      @keydown="handleKeyDown"
    />

    <ProgressBar
      v-if="inProgress"
      class="-mb-0.5 h-0.5 w-full"
      mode="indeterminate"
      pt:value="bg-brand-400"
    />
  </div>
</template>
